/**
 * MemberDetailPanelProvider — one panel, opened from anywhere.
 *
 * Wrap the club admin route subtree once. Any page nested below can call
 * useMemberDetailPanel().openMemberPanel(memberId) and the same panel slides in.
 *
 * The member roster is fetched lazily via useMembersProgress (cached per-club,
 * React Query dedupes against pages that already use the hook).
 */

import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useMembersProgress } from '@/hooks/useMemberProgress';
import { MemberDetailPanel } from './MemberDetailPanel';

interface PanelContextValue {
  openMemberPanel: (memberId: string) => void;
  closeMemberPanel: () => void;
  isOpen: boolean;
}

const MemberDetailPanelContext = createContext<PanelContextValue | null>(null);

export const useMemberDetailPanel = (): PanelContextValue => {
  const ctx = useContext(MemberDetailPanelContext);
  if (!ctx) {
    // Safe no-op if used outside the provider
    return {
      openMemberPanel: () => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[useMemberDetailPanel] called outside MemberDetailPanelProvider — no-op');
        }
      },
      closeMemberPanel: () => {},
      isOpen: false,
    };
  }
  return ctx;
};

interface Props {
  clubId: string | undefined;
  children: React.ReactNode;
}

export const MemberDetailPanelProvider: React.FC<Props> = ({ clubId, children }) => {
  const [openMemberId, setOpenMemberId] = useState<string | null>(null);

  // Only fetch the roster when the panel is opened.
  const { data } = useMembersProgress(openMemberId ? clubId : undefined, undefined);

  const openMemberPanel = useCallback((memberId: string) => {
    setOpenMemberId(memberId);
  }, []);

  const closeMemberPanel = useCallback(() => {
    setOpenMemberId(null);
  }, []);

  const value = useMemo(
    () => ({ openMemberPanel, closeMemberPanel, isOpen: openMemberId !== null }),
    [openMemberPanel, closeMemberPanel, openMemberId]
  );

  const member = openMemberId && data?.members
    ? data.members.find(m => m.member_id === openMemberId) ?? null
    : null;

  return (
    <MemberDetailPanelContext.Provider value={value}>
      {children}
      {openMemberId && <MemberDetailPanel member={member} onClose={closeMemberPanel} />}
    </MemberDetailPanelContext.Provider>
  );
};
